import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'api/client';
import { pipelineKeys } from 'api/pipelines';

// ── Query keys ──────────────────────────────────────────────────────────────
export const connectorKeys = {
  types: ['connector-types'],
  connections: ['connections'],
  connection: (id) => ['connections', id],
  task: (id) => ['tasks', id],
};

// ── Connector Types (catalog) ────────────────────────────────────────────────
export function useConnectorTypes() {
  return useQuery({
    queryKey: connectorKeys.types,
    queryFn: () => apiClient.get('/api/v1/connectors/types/').then((r) => r.data),
    staleTime: 1000 * 60 * 60, // the catalog rarely changes
  });
}

// ── Connections ──────────────────────────────────────────────────────────────
export function useConnections() {
  return useQuery({
    queryKey: connectorKeys.connections,
    queryFn: () => apiClient.get('/api/v1/connectors/connections/').then((r) => r.data),
    refetchInterval: 60000, // surface offline connections without a manual refresh
  });
}

export function useConnection(id) {
  return useQuery({
    queryKey: connectorKeys.connection(id),
    queryFn: () => apiClient.get(`/api/v1/connectors/connections/${id}/`).then((r) => r.data),
    enabled: !!id,
    refetchInterval: (query) => {
      // Poll while a create/test is in flight, until 'pending' flips.
      const data = query.state.data;
      return data?.status === 'pending' ? 2000 : false;
    },
  });
}

/** The source's last month: bytes it billed per day and its access outages. */
export function useConnectionActivity(id) {
  return useQuery({
    queryKey: ['connections', id, 'activity'],
    queryFn: () => {
      let tz = 'UTC';
      try {
        tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      } catch {
        // keep UTC
      }
      return apiClient
        .get(`/api/v1/connectors/connections/${id}/activity/`, { params: { tz } })
        .then((r) => r.data);
    },
    enabled: !!id,
  });
}

export function useCreateConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/api/v1/connectors/connections/', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: connectorKeys.connections }),
  });
}

export function useUpdateConnection(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.patch(`/api/v1/connectors/connections/${id}/`, data).then((r) => r.data),
    onSuccess: () => {
      // A config change resets the connection to 'pending' and re-tests it, and
      // the re-test may pause/resume pipelines once the status settles.
      qc.invalidateQueries({ queryKey: connectorKeys.connections });
      qc.invalidateQueries({ queryKey: connectorKeys.connection(id) });
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: connectorKeys.connection(id) });
        qc.invalidateQueries({ queryKey: pipelineKeys.all });
      }, 3000);
    },
  });
}

export function useDeleteConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/api/v1/connectors/connections/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: connectorKeys.connections }),
  });
}

export function useTestConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      apiClient.post(`/api/v1/connectors/connections/${id}/test/`).then((r) => r.data),
    onSuccess: (_, id) => {
      // Let the new status and its post_save cascade (which may pause/resume
      // pipelines) settle before refreshing.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: connectorKeys.connection(id) });
        qc.invalidateQueries({ queryKey: connectorKeys.connections });
        qc.invalidateQueries({ queryKey: pipelineKeys.all });
      }, 3000);
    },
  });
}

// Atomic wizard save (EVOLV-126): ONE POST creates the connection and every
// schedule-group pipeline, or nothing at all — the server rolls the whole
// request back on any validation failure, so no half-configured connection
// can be stranded.
export function useCreateFileSources() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post('/api/v1/connectors/file-sources/', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: connectorKeys.connections });
      // The wizard lands on /console/pipelines, so the cached list must not
      // still be serving pre-save rows.
      qc.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

// ── File sources (EVOLV-126 wizard) ──────────────────────────────────────────
// Stateless, bounded S3 listing for the wizard's bucket check and file preview;
// nothing is saved until the wizard's final step. Returns {reachable, error,
// matched_count, sample, truncated} — a failure is a RESULT (reachable: false +
// a fixed error class), not an exception; only transport/authz problems reject.
// In edit mode with the secret left blank, pass connectionId instead: the probe
// runs on the SAVED config server-side, since the client never has that secret.
export function useProbeBucket() {
  return useMutation({
    mutationFn: ({
      config,
      prefix = '',
      connectionId = null,
      overrides = null,
      // Ask for top-level folders so step 2 can offer them for selection
      // instead of asking anyone to type a path from memory.
      folders = false,
      columns = null,
    }) =>
      connectionId
        ? apiClient
            .post(`/api/v1/connectors/connections/${connectionId}/probe-bucket/`, {
              prefix,
              folders,
              ...(columns ? { columns } : {}),
              // Edited non-secret fields; the server layers them over the SAVED
              // credentials so the probe tests what a save would persist.
              ...(overrides ? { overrides } : {}),
            })
            .then((r) => r.data)
        : apiClient
            .post('/api/v1/connectors/probe-bucket/', {
              config,
              prefix,
              folders,
              ...(columns ? { columns } : {}),
            })
            .then((r) => r.data),
  });
}

// ── BigQuery source wizard (S3/S4) ───────────────────────────────────────────
// Discovery, one wizard step at a time: nothing named lists the datasets, a
// dataset lists its tables (daily shards collapsed into one family card), a
// table describes its columns, suggests a window axis and prices a day.
//
// Reads no data and bills nothing — metadata is free and the day estimate is a
// dry run. Failures are a RESULT ({reachable: false, error, hint}), not an
// exception, so a wrong key is a message on the step, not a crash.
//
// Stateless by default, for the same reason as the bucket probe: the wizard
// persists nothing until its last step. In edit mode the stored key cannot be
// sent from the browser (it never serializes out), so the call goes to the
// saved connection instead, with the non-secret fields being edited layered on
// top as overrides.
export function useProbeBigQuery() {
  return useMutation({
    mutationFn: ({
      config = null,
      connectionId = null,
      overrides = null,
      dataset = '',
      table = '',
      windowColumn = '',
    }) => {
      const asked = { dataset, table, window_column: windowColumn };
      return connectionId
        ? apiClient
            .post(`/api/v1/connectors/connections/${connectionId}/probe-bigquery/`, {
              ...asked,
              ...(overrides ? { overrides } : {}),
            })
            .then((r) => r.data)
        : apiClient
            .post('/api/v1/connectors/probe-bigquery/', { config, ...asked })
            .then((r) => r.data);
    },
  });
}

// The wizard's single save (D9): ONE POST creates the connection and its
// pipeline, or neither. A backfill runs only when the body carries one (D10).
export function useCreateBigQuerySource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post('/api/v1/connectors/bigquery-sources/', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: connectorKeys.connections });
      qc.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

// ── AppMetrica source wizard ─────────────────────────────────────────────────
// The static export catalog: tables, fields, types, defaults, time columns.
export function useAppMetricaCatalog() {
  return useQuery({
    queryKey: ['appmetrica-catalog'],
    queryFn: () => apiClient.get('/api/v1/connectors/appmetrica-catalog/').then((r) => r.data),
    staleTime: 1000 * 60 * 60,
  });
}

// Management API lookup: with nothing named, the apps the token can see; with
// an application id, that app. Failures are a RESULT ({reachable: false,
// error, hint}), not an exception. The token comes from the grant (resolved
// server-side), from `config` when typed by hand, or from the saved connection
// in edit mode.
export function useProbeAppMetrica() {
  return useMutation({
    mutationFn: ({ config = null, grantId = null, connectionId = null, applicationId = '' }) =>
      connectionId
        ? apiClient
            .post(`/api/v1/connectors/connections/${connectionId}/probe-appmetrica/`, {
              application_id: applicationId,
            })
            .then((r) => r.data)
        : apiClient
            .post('/api/v1/connectors/probe-appmetrica/', {
              ...(config ? { config } : {}),
              ...(grantId ? { oauth_grant_id: grantId } : {}),
              application_id: applicationId,
            })
            .then((r) => r.data),
  });
}

// The wizard's single save (D9): ONE POST creates the connection and its
// pipeline, or neither. A backfill runs only when the body carries one (D10).
export function useCreateAppMetricaSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post('/api/v1/connectors/appmetrica-sources/', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: connectorKeys.connections });
      qc.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

// ── Yandex Direct source wizard ──────────────────────────────────────────────
// The static stream catalog: streams with their API point costs, defaults and
// the connection's choices (VAT, attribution model, ceilings).
export function useDirectCatalog() {
  return useQuery({
    queryKey: ['direct-catalog'],
    queryFn: () => apiClient.get('/api/v1/connectors/direct-catalog/').then((r) => r.data),
    staleTime: 1000 * 60 * 60,
  });
}

// Who the token can load: the account and, for an agency, its clients with
// their daily API points. Failures are a RESULT ({reachable: false, error,
// hint}), not an exception. The token comes from the grant (resolved
// server-side), from `config` when typed by hand, or from the saved connection
// in edit mode. The server caches a listing for five minutes; `refresh` skips
// that cache.
export function useProbeDirect() {
  return useMutation({
    mutationFn: ({ config = null, grantId = null, connectionId = null, refresh = false }) =>
      connectionId
        ? apiClient
            .post(`/api/v1/connectors/connections/${connectionId}/probe-direct/`, { refresh })
            .then((r) => r.data)
        : apiClient
            .post('/api/v1/connectors/probe-direct/', {
              ...(config ? { config } : {}),
              ...(grantId ? { oauth_grant_id: grantId } : {}),
              refresh,
            })
            .then((r) => r.data),
  });
}

// The wizard's single save: ONE POST creates the connection and its pipeline,
// or neither. A backfill runs only when the body carries one.
export function useCreateDirectSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post('/api/v1/connectors/direct-sources/', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: connectorKeys.connections });
      qc.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

// Atomic wizard edit: the connection and every schedule-group pipeline change
// together or not at all.
export function useUpdateFileSources() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) =>
      apiClient.patch(`/api/v1/connectors/file-sources/${id}/`, data).then((r) => r.data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: connectorKeys.connections });
      qc.invalidateQueries({ queryKey: connectorKeys.connection(id) });
      qc.invalidateQueries({ queryKey: pipelineKeys.all });
    },
  });
}

// Fixed template-domain list for dbt_source_key (server-enforced; the report
// catalog is the single source of truth).
export function useSourceDomains() {
  return useQuery({
    queryKey: ['source-domains'],
    queryFn: () =>
      apiClient.get('/api/v1/connectors/source-domains/').then((r) => r.data.domains),
    staleTime: 1000 * 60 * 60,
  });
}

// ── Task polling ─────────────────────────────────────────────────────────────
export function useTaskStatus(taskId) {
  return useQuery({
    queryKey: connectorKeys.task(taskId),
    queryFn: () => apiClient.get(`/api/v1/tasks/${taskId}/`).then((r) => r.data),
    enabled: !!taskId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2000;
      if (data.status === 'SUCCESS' || data.status === 'FAILURE') return false;
      return 2000;
    },
  });
}
